import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAgeGate } from "@/context/AgeGateContext";
import { ShieldCheck, AlertTriangle } from "lucide-react";

export default function AgeGate() {
  const { verified, ready, confirm } = useAgeGate();
  const [denied, setDenied] = useState(false);

  if (!ready || verified) return null;

  return (
    <Dialog open={true}>
      <DialogContent
        data-testid="age-gate-modal"
        className="max-w-md p-0 overflow-hidden border-slate-200 rounded-md [&>button]:hidden"
      >
        <div className="p-8">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-white" strokeWidth={1.75} />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-blue-700">
              Age Verification · 21+
            </div>
          </div>

          <DialogHeader className="text-left space-y-3">
            <DialogTitle className="font-display text-3xl leading-tight tracking-tight text-slate-900">
              Are you 21 or older?
            </DialogTitle>
            <DialogDescription className="text-slate-600 text-sm leading-relaxed">
              Peps 4 Pets provides <span className="text-slate-900 font-medium">strictly informational</span> research
              content about peptides intended for veterinary research use. You must be 21 years of age or older to
              access this site. Nothing on this site is medical advice.
            </DialogDescription>
          </DialogHeader>

          {denied && (
            <div
              data-testid="age-gate-denied"
              className="mt-5 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5" strokeWidth={1.75} />
              <span>Access denied. You must be 21 or older to enter this site.</span>
            </div>
          )}

          <div className="mt-8 flex flex-col gap-2">
            <Button
              data-testid="age-gate-confirm-button"
              onClick={confirm}
              className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium"
            >
              I am 21 or older — Enter site
            </Button>
            <Button
              data-testid="age-gate-deny-button"
              variant="ghost"
              onClick={() => setDenied(true)}
              className="w-full h-11 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-md"
            >
              I am under 21
            </Button>
          </div>

          <p className="mt-6 text-[11px] leading-relaxed text-slate-400 border-t border-slate-100 pt-4">
            By entering, you acknowledge all content is for research and educational purposes only. Not for human
            consumption. Not evaluated by the FDA.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
